import React, { useState, useEffect, useLayoutEffect } from "react"
import { Link, PageProps } from "gatsby"
import gsap from "gsap"
import ScrollTrigger from "gsap/ScrollTrigger"
import SplitText from "gsap/SplitText"
import Layout from "../components/hoc/Layout"
import SEO from "../components/seo"

import illustration1 from "../assets/illustrations/1.svg"
import illustration2 from "../assets/illustrations/2.svg"
import illustration3 from "../assets/illustrations/3.svg"
import illustration4 from "../assets/illustrations/4.svg"
import illustration5 from "../assets/illustrations/5.svg"
import avatar from "../assets/images/avatar.jpg"

import styles from "./index.module.scss"
import TextButton, { BUTTON_TYPE } from "../components/textButton/TextButton"

import tickIcon from "../assets/icons/tick.svg"
import circleArrowIcon from "../assets/icons/circle-arrow.svg"
import linkedInIcon from "../assets/icons/linkedin.svg"
import instagramIcon from "../assets/icons/instagram.svg"
import githubIcon from "../assets/icons/github.svg"
import gmailIcon from "../assets/icons/gmail.svg"
// import stackblitzIcon from "../assets/icons/linkedin.svg"

gsap.registerPlugin(ScrollTrigger)

// https://www.netlifycms.org/docs/gatsby//

const IndexPage: React.FC<PageProps> = () => {
  const TAGS = ["Productivity", "Wellbeing", "Business"]

  // Heading Text
  useLayoutEffect(() => {
    const h1Split = new SplitText("#heading")

    // gsap.set("#heading", { opacity: 1 })
    // gsap.to(h1Split.chars, {
    //   opacity: 1,
    //   duration: 1,
    //   ease: "ease-in",
    //   stagger: { from: "random", amount: 0.5 },
    // })

    gsap.to(h1Split.chars, {
      opacity: 0,
      ease: "none",
      duration: 1,
      stagger: { from: "random", amount: 0.5 },
      scrollTrigger: {
        trigger: "#heading",
        scrub: true,
        start: 0,
        end: "center top",
      },
    })
  }, [])

  // Parallax Images
  useEffect(() => {
    const images: HTMLImageElement[] = gsap.utils.toArray(".parallax-img")
    images.forEach((img, index) =>
      gsap.to(img, {
        scale: 1.15,
        rotate: index % 2 ? -3 : 3,
        yPercent: -33,
        scrollTrigger: {
          trigger: img,
          start: "top bottom",
          end: "bottom top",
          scrub: 3,
        },
      })
    )
    ScrollTrigger.addEventListener("refreshInit", () =>
      gsap.set(".parallax-img", { yPercent: 0 })
    )
  }, [])

  useEffect(() => {
    // Footer Contact Icons
    gsap.to(".connect-icon", {
      rotate: -360,
      duration: 20,
      repeat: -1,
      ease: "none",
    })
    gsap.to("#connect-icons", {
      rotate: 360,
      duration: 20,
      repeat: -1,
      ease: "none",
    })
  }, [])

  return (
    <Layout>
      <SEO title="Welcome" />

      <header id="header" className={styles.header}>
        <h1 id="heading">
          I help ambitious businesses plan and deliver digital products.
        </h1>
        <a href="mailto:mattfrawley1@gmail.com">
          <TextButton label="Discuss a Project" type={BUTTON_TYPE.PRIMARY} />
        </a>
      </header>

      <section className={styles.passionSection}>
        <img
          className="parallax-img"
          src={illustration1}
          alt="Matthew Frawley Working Illustration"
        />
        <div>
          <p className="sans-serif">
            I’m passionate about creating solutions that elevate human health
            and performance.
          </p>
          <div className={styles.tags}>
            {TAGS.map(tag => (
              <CheckBox key={tag} text={tag} />
            ))}
          </div>
        </div>
      </section>

      <section className={styles.benefits}>
        <h2>With bespoke technology, your business can:</h2>
        <ul>
          <li className="theme-card-green">
            <h3>Do more with what you have</h3>
            <span>
              Operate more efficiently by automating mundane tasks and
              streamlining processes.
            </span>
          </li>
          <li className="theme-card-green">
            <h3>Create new revenue streams</h3>
            <span>
              Turn your service and knowledge into a product, or launch
              something brand new!
            </span>
          </li>
          <li className="theme-card-green">
            <h3>Deliver a superior customer experience</h3>
            <span>Improve your customer conversion and retention rates.</span>
          </li>
        </ul>
      </section>

      <section className={styles.focusSection}>
        <div>
          <p className="sans-serif green">
            Focus your team.
            <br />
            Focus your app.
            <br />
            Start simple.
          </p>
        </div>
        <img
          className="parallax-img"
          src={illustration2}
          alt="Archer Illustration"
        />
      </section>

      <section className={styles.process}>
        <span className={`${styles.serviceTitle} pink`}>Process</span>
        <h2 className="li">Together we will:</h2>

        <div>
          <img
            className="parallax-img"
            src={illustration3}
            alt="People working together"
          />

          <div className={styles.processCards}>
            {PROCESS.map((processStep, i) => (
              <ProcessCard key={process.title} process={processStep} i={i} />
            ))}
          </div>
        </div>
      </section>

      <section className={styles.innovateSection}>
        <p className="sans-serif pink">
          Keep testing.
          <br />
          Keep iterating.
          <br />
          Keep evolving.
        </p>
        <img className="parallax-img" src={illustration4} alt="App Designer" />
      </section>

      <section className={styles.services}>
        <span className={`${styles.serviceTitle} green`}>Services</span>

        <div className={styles.serviceCards}>
          {SERVICES.map(service => (
            <ServiceCard key={service.title} service={service} />
          ))}
        </div>

        <img
          className="parallax-img"
          src={illustration5}
          alt="Man analysing data"
        />
      </section>

      <footer className={`${styles.footer} theme-card border-yellow`}>
        <h4 className={`${styles.serviceTitle} yellow`}>Connect</h4>

        <div className={styles.avatarContainer}>
          <img className={styles.avatar} src={avatar} alt="Matthew Frawley" />
          <div id="connect-icons">
            {CONTACT_OPTIONS.map((option, i) => {
              const rotate = (360 / CONTACT_OPTIONS.length) * i
              return (
                <a
                  key={option.title}
                  href={option.href}
                  title={option.title}
                  className={`${styles.icon} border-yellow connect-icon`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    transform: `rotate(${rotate}deg) translateY(-120px) rotate(-${rotate}deg)`,
                  }}
                >
                  <img src={option.icon} alt={option.title} />
                </a>
              )
            })}
          </div>
        </div>
        <h4>A web and app designer who loves to build things.</h4>
        <p>
          A design-led frontend developer with a passion for creating products
          that use technology to help people. Quick to understand problems and
          visualise solutions. Constantly sharpening his saw.
        </p>
      </footer>
    </Layout>
  )
}

export default IndexPage

const CheckBox: React.FC<{ text: string }> = ({ text }) => {
  return (
    <div className={`${styles.checkbox} border-yellow`} key={text}>
      <span>{text}</span>
      <div className="background-yellow">
        <img src={tickIcon} alt="Check" />
      </div>
    </div>
  )
}

type ProcessStep = {
  title: string
  text: string
}
const PROCESS: ProcessStep[] = [
  {
    title: "Understand and Plan",
    text:
      "Clarify what’s most important, and form a compelling product vision.",
  },
  {
    title: "Design and Create",
    text:
      "Work with a high-performing team that care about the quality of their work and the goals of your business.",
  },
  {
    title: "Iterate and Make Great",
    text:
      "Follow an agile approach to build, test and learn from your audience.",
  },
]

type ProcessCardProps = {
  process: ProcessStep
  i: number
}

const ProcessCard: React.FC<ProcessCardProps> = ({ process, i }) => {
  return (
    <div className={`${styles.processCard} theme-card-pink`}>
      <div className="background-pink">{i + 1}</div>
      <div>
        <h5>{process.title}</h5>
        <p>{process.text}</p>
      </div>
    </div>
  )
}

type ServiceCardProps = {
  service: Service
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service }) => {
  // const [isOpen, setIsOpen] = useState(false)

  return (
    <div
      key={service.title}
      className={`${styles.serviceCard} theme-card-green`}
    >
      {/* Header Block */}
      <div className="background-green-transparent">
        <h4>{service.title}</h4>
        <a href="mailto:mattfrawley1@gmail.com" title="Mail Matt">
          <img src={circleArrowIcon} alt="Learn More" />
        </a>
      </div>
      {/* Info Block */}
      <p>{service.question}</p>
    </div>
  )
}

type Service = {
  title: string
  question: string
  description: string
}

const SERVICES: Service[] = [
  {
    title: "Full-stack app development with Loopspeed",
    question:
      "Looking for a team to help you launch something awesome? Look no further.",
    description: "",
  },

  {
    title: "Contract React and React Native App Development",
    question:
      "Does your interface need a facelift? How would an improved design look?",
    description: "",
  },
  {
    title: "Rapid Prototype Production",
    question:
      "A cost-effective way to test your ideas and start bringing the product to life.",
    description:
      "Prototyping is a cost-effective way to test your ideas and begin bringing your product to life. I can help by working with you to build one. You can use the protoype to gather feedback or use in demos.",
  },
  {
    title: "App Review and Critique",
    question:
      "How might objective feedback from a specialist improve your product?",
    description:
      "I will listen to your goals and understand what you're trying to achieve before reviewing the app and providing helpful suggestions. My focus will be on usability and design.",
  },
]

const CONTACT_OPTIONS = [
  {
    title: "Follow on Instagram",
    icon: instagramIcon,
    href: "https://instagram.com/loopspeedapps",
  },
  {
    title: "View on Github",
    icon: githubIcon,
    href: "https://github.com/matthewfrawley",
  },
  {
    title: "Connect on LinkedIn",
    icon: linkedInIcon,
    href: "https://www.linkedin.com/in/matthewjfrawley/",
  },
  {
    title: "Email Matt",
    icon: gmailIcon,
    href: "mailto:mattfrawley1@gmail.com",
  },
]

// About me - my values

// My interests span psychology, business and
// psychology

// I help ambitious businesses plan and deliver digital products.

// I’m especially passionate about creating solutions to improve human health and performance.
// [Productivity, Wellbeing, Business]

// With a rockstar app your business can:
// - Be more efficient at scale and do more with what you have
// - Turn a service into a product and generate new revenue
// - Deliver a better customer experience and convert more online

// With my help you can:

// 1. Get clear on what’s important and form a compelling vision
// 2. Assemble a high-performing team that care deeply about the goals of the business
// 3. Follow an agile approach to build, test and learn from your audience

// SERVICES
// React and React Native App Development
// (daily - £800 +VAT)*/

/* Full-stack product development with Loopspeed 
{
  /* UX and UI review */

/* Interactive product protoype */
